import React, { ReactElement } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ConditionalWrapper } from 'components/common/ConditionalWrapper';
import { SuccessIcon } from 'assets/icons/v2';
import { SubscriptionButtonBorder } from './SubscriptionButtonBorder';

type Props = {
  isActive: boolean;
  onClick: () => void;
  duration: string;
  price?: string;
  pricePerMonth: string;
  additionalInfo?: string;
  oldMonthlyPricing?: string;
  savedPercentage?: number;
  hasPromoCode?: boolean;
  hasReferral?: boolean;
};

export const SubscriptionButton = ({
  isActive,
  onClick,
  duration,
  price,
  pricePerMonth,
  additionalInfo,
  oldMonthlyPricing,
  savedPercentage,
  hasPromoCode,
  hasReferral,
}: Props): ReactElement => {
  const renderButtonBorder = (children: React.ReactNode): ReactElement => (
    <SubscriptionButtonBorder percentage={savedPercentage}>{children}</SubscriptionButtonBorder>
  );
  const shouldShowNudging = !!savedPercentage && !isActive && hasPromoCode;

  return (
    <ConditionalWrapper condition={isActive} wrapper={renderButtonBorder}>
      <Box px={!isActive ? '10px' : '0'} onClick={onClick}>
        <Box
          width="100%"
          borderRadius="10px"
          bg={isActive ? 'background.accent' : 'tag.background.green'}
          border="default"
          borderColor="border.accent.active"
          p="15px"
          cursor="pointer"
          position="relative"
        >
          {shouldShowNudging && (
            <Text
              color="text.primary"
              bg="background.subtle.green"
              borderRadius="5px"
              border="default"
              borderColor="border.accent.active"
              position="absolute"
              top="-9px"
              right="l"
              textAlign="center"
              lineHeight="100%"
              py="3xs"
              px="xs"
              textStyle="body.xs.regular"
            >
              Spare {savedPercentage} %
            </Text>
          )}
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            color={isActive ? 'text.onAccent' : 'text.primary'}
          >
            <Flex flexDirection="column">
              <Text textStyle="body.m.medium">{duration}</Text>
              {price && <Text textStyle="body.m.medium">{price}</Text>}
            </Flex>
            <Flex flexDirection="column">
              <Text textStyle="body.m.medium">{pricePerMonth}</Text>
              {oldMonthlyPricing && (
                <Text as="s" textStyle="body.s.medium">
                  {oldMonthlyPricing}
                </Text>
              )}
            </Flex>
          </Flex>
          {additionalInfo && (
            <Text
              textStyle="body.m.medium"
              color={isActive ? 'text.default' : 'text.primary'}
              fontWeight="light"
            >
              {additionalInfo}
            </Text>
          )}
        </Box>
      </Box>
      {hasPromoCode && isActive && (
        <Box pl="15px" pt="10px" display="flex" flexDir="row" gap="5px" alignItems="center">
          <Text textStyle="body.xs.regular" color="text.tertiary">
            Dein Promo Code ist aktiv
          </Text>
          <SuccessIcon color="icon.tertiary" height="12px" width="12px" />
        </Box>
      )}
      {!hasPromoCode && isActive && hasReferral && (
        <Box pl="15px" pt="10px" display="flex" flexDir="row" gap="5px" alignItems="center">
          <Text textStyle="body.xs.regular" color="text.tertiary">
            Dein Freundschafts-Code ist aktiv
          </Text>
          <SuccessIcon color="icon.tertiary" height="12px" width="12px" />
        </Box>
      )}
    </ConditionalWrapper>
  );
};
